import {
  Button,
  Card,
  Col,
  Form,
  Input,
  notification,
  Row,
  Select,
  Table,
} from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getTempEngagementFormData,
  getUserList,
  getUserInfo,
  getHigherOfficials,
  getLiveConversionRate,
} from "redux/selectors";
import styled from "styled-components";
import { Required } from "./index.js";
import {
  useHistory,
} from "react-router-dom/cjs/react-router-dom.min.js";
import { useRouteState } from "hooks/useRouteState";
import queryKeys from "common/queryKey.js";
import { useFetch } from "hooks/useFetch.js";
import { sumBy } from "lodash";
import { billingOffice, currencyKeyMap } from "common/Constants";
import ResourcePlan from "../EngagementView/ResourcePlan.js";
import SubContractor from "../SubContractor/index.js";
import { currencyAmount, getEmailsByRole } from "utils/functions.js";
import { useMutate } from "hooks/useMutate";
import currencyMasterData from "common/currencyRateMasterData.json";
import { CALL_FEATCH_API } from "common/API.js";
import { setCurrencyList, setLiveConversionRate } from "redux/actions.js";

import userModifiedCache from "./engagementModifiedCache.js";

const labelStyle = {
  width: "150px",
  display: "inline-block",
  textAlign: "start",
};

const FinancialResourcePlanFields = ({
  update,
  setDeletedbookedUserId,
  disabled,
  addResourceandSubDisabled,
  localAddresourceDisable,
  tabId,
  setIsResource,
  isResource,
}) => {
  const form = Form.useFormInstance();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useRouteState(["id"], { persistTo: "query" });

  const tempEngagementFormData = useSelector((state) =>
    getTempEngagementFormData(state)
  );
  const userList = useSelector((state) => getUserList(state));
  const [budjetChargeout, setBudjetChargeout] = useState(0);
  const [showSubContractorModal, setShowSubContractorModal] = useState(false);
  const [plannedSubcontractor, setPlannedSubcontractor] = useState(0);
  const [tableMandays, setTableMandays] = useState(0);

  const [currencyValueList, setCurrencyValueList] = useState([]);
  const userInfo = useSelector(getUserInfo);
  const higherOfficialsList = useSelector((state) => getHigherOfficials(state));
  const engagementHigherUsers = getEmailsByRole(higherOfficialsList, "engagementHigherUsers");
  const currentUserData = userList?.filter(
    (item) => item.userid === userInfo?.userid
  );
  const convertionRate = useSelector(getLiveConversionRate);

  // ─── Refs ───────────────────────────────────────────────────────────────────
  // ─── Refs ───────────────────────────────────────────────────────────────────
  const isUserCurrencyChange = useRef(false);
  const hasUserModified = useRef(userModifiedCache.has(id));

  const userCountry = currentUserData[0]?.managingoffice[0];

  // ─── Sync live conversion rate from Redux (only if user hasn't manually changed it) ───
  useEffect(() => {
    if (isUserCurrencyChange.current) return;
    if (hasUserModified.current) return;

    if (
      convertionRate &&
      Number(convertionRate) !== Number(form.getFieldValue("conversion_rate"))
    ) {
      form.setFieldsValue({
        conversion_rate: Number(convertionRate),
      });
    }
  }, [convertionRate]);

  const billingOfficeOptions = billingOffice
    .filter((office) => {
      if (userCountry === "Head Office") {
        return office;
      } else {
        return (
          office.name.includes(userCountry) ||
          (office.id === 18 && office.name === "BDO Africa Holdings Ltd") ||
          (userCountry === "Uganda" && (office.id === 19 || office.id === 15) ||
            (userCountry === "Republic of Congo" && (office.id === 20 || office.id === 21 || office.id === 22)) ||
            (userCountry === "Burundi" && (office.id === 20 || office.id === 21 || office.id === 22)) ||
            (userCountry === "Cameroon" && (office.id === 23 || office.id === 24)) || 
            (userCountry === "Ghana" && (office.id === 25 || office.id === 26)) ||
            (userCountry === "Senegal" && (office.id === 27 || office.id === 28))
          )
        );
      }
    })
    .map((office) => ({
      label: office.name,
      value: office.id,
    }));

  const currencyOptions = currencyMasterData.map((currency) => ({
    label: currency.TBD_DESC,
    value: currency.TBD_DESC,
  }));

  const formValues = form.getFieldsValue(true);

  const handleCurrencyChange = (value) => {
    const rawValue = String(value || "").trim();
    const selectedMaster = (currencyMasterData || []).find((c) => {
      const desc = String(c?.TBD_DESC || "").trim().toLowerCase();
      const id = String(c?.TBD_ID || "").trim().toLowerCase();
      return desc === rawValue.toLowerCase() || id === rawValue.toLowerCase();
    });

    let latestEntry = currencyValueList?.[0] || {};
    const candidates = [];
    if (selectedMaster?.TBD_ID) candidates.push(selectedMaster.TBD_ID.toLowerCase());
    if (selectedMaster?.TBD_DESC) candidates.push(selectedMaster.TBD_DESC.toLowerCase().replace(/\s+/g, "_"));
    candidates.push(rawValue.toLowerCase().replace(/\s+/g, "_"));
    candidates.push(rawValue.toLowerCase());

    latestEntry = Object.fromEntries(
      Object.entries(latestEntry).map(([key, val]) => [key.toLowerCase(), val])
    );

    let dynamicRateFromAPI;
    for (const k of candidates) {
      if (!k) continue;
      if (Object.prototype.hasOwnProperty.call(latestEntry, k)) {
        const v = latestEntry[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") {
          dynamicRateFromAPI = v;
          break;
        }
      }
    }

    const conversionRateFromMaster = selectedMaster?.TBD_ABBR;
    const finalRate = Number(dynamicRateFromAPI ?? conversionRateFromMaster);
    if (!Number.isFinite(finalRate)) return;

    const safeFinalRate = Number.isFinite(finalRate) ? finalRate : 1;
    form.setFieldsValue({ conversion_rate: safeFinalRate });
    dispatch(setLiveConversionRate(safeFinalRate));

    userModifiedCache.set(id, {
      ...(userModifiedCache.get(id) || {}),
      currency: value,
      conversion_rate: safeFinalRate,
    });

    const billingAmount = Number(form.getFieldValue("billing_amount") || 0);
    const plannedFeeAdjustment = Number(form.getFieldValue("planned_fee_adjustment") || 0);
    convertToUSD(billingAmount, safeFinalRate, "usd_amount");
    convertToUSD(plannedFeeAdjustment, safeFinalRate, "pfa_usd");
  };

  const handleBillingAmountChange = async (e) => {
    const billingAmount = e.target.value;
    const conversionRate = form.getFieldValue("conversion_rate");

    hasUserModified.current = true;
    userModifiedCache.set(id, {
      ...(userModifiedCache.get(id) || {}),
      billing_amount: billingAmount,
    });

    convertToUSD(billingAmount, conversionRate, "usd_amount");
    await profitCals();
  };

  const handleConversionRateChange = async (e) => {
    hasUserModified.current = true;
    const conversionRate = e.target.value;

    userModifiedCache.set(id, {
      ...(userModifiedCache.get(id) || {}),
      conversion_rate: conversionRate,
    });

    dispatch(setLiveConversionRate(conversionRate));
    const billingAmount = form.getFieldValue("billing_amount");
    const plannedFeeAdjustment = form.getFieldValue("planned_fee_adjustment");

    convertToUSD(billingAmount, conversionRate, "usd_amount");
    convertToUSD(plannedFeeAdjustment, conversionRate, "pfa_usd");
    await profitCals();
  };

  const convertToUSD = (amount, conversionRate, targetField) => {
    if (amount && conversionRate) {
      const usdAmount = parseFloat(amount) * parseFloat(conversionRate);
      form.setFieldsValue({ [targetField]: Number(usdAmount.toFixed(2)) });
    } else {
      form.setFieldsValue({ [targetField]: "" });
    }
  };

  const {
    getEngagementById,
    getBillingById,
    getTimeSheetByengagementId,
    addEngagement: addEngagementQuery,
  } = queryKeys;

  const { key: addEngagementKey, url: addEngagementUrl } = addEngagementQuery(true);
  const { mutate: addEngagement, isLoading: isPosting } = useMutate(addEngagementKey, addEngagementUrl);

  const { key, url } = getEngagementById(id);
  const { data: engagementDataRes, refetch: engagementRefetch } = useFetch(key, url, { enabled: !!id });
  const engagementData = engagementDataRes?.data;

  const [profitLoss, setProfitLoss] = useState(() => {
    const cached = userModifiedCache.get(id);
    return cached?.estimated_profit ?? 0;
  });

  // ─── Patch form values on load ───────────────────────────────────────────────
  useEffect(() => {
    if (!engagementData) return;

    const cached = userModifiedCache.get(id);

    if (cached) {
      const restore = {};
      if (cached.usd_amount !== undefined) restore.usd_amount = cached.usd_amount;
      if (cached.pfa_status !== undefined) restore.pfa_status = cached.pfa_status;
      if (cached.planned_fee_adjustment !== undefined) restore.planned_fee_adjustment = cached.planned_fee_adjustment;
      if (cached.pfa_usd !== undefined) restore.pfa_usd = cached.pfa_usd;
      if (cached.billing_amount !== undefined) restore.billing_amount = cached.billing_amount;
      if (cached.conversion_rate !== undefined) restore.conversion_rate = cached.conversion_rate;
      if (cached.currency !== undefined) restore.currency = cached.currency;
      form.setFieldsValue(restore);

      if (cached.estimated_profit !== undefined) setProfitLoss(cached.estimated_profit);
      if (cached.conversion_rate !== undefined) dispatch(setLiveConversionRate(cached.conversion_rate));
    } else {
      // FIX #1: safe fallback chain — all known API field names tried in order
      const apiPfa =
        engagementData?.financial_resourcePlan?.pfa_status ||
        engagementData?.financial_resourcePlan?.pfaStatus ||
        engagementData?.financial_resourcePlan?.pfastatus ||
        engagementData?.financial_resourcePlan?.PFA_Status ||
        engagementData?.financial_resourcePlan?.PFAStatus ||
        "Not Applicable";
      form.setFieldsValue({ pfa_status: apiPfa });
    }
  }, [engagementData]);

  // ─── Restore conversion rate from API (only if user hasn't touched currency) ─
  useEffect(() => {
    if (!engagementData) return;
    if (isUserCurrencyChange.current) return;
    if (hasUserModified.current) return;

    const savedRate =
      engagementData?.financial_resourcePlan?.conversionRate ??
      engagementData?.financial_resourcePlan?.conversion_rate;

    if (
      savedRate &&
      Number(savedRate) !== Number(form.getFieldValue("conversion_rate"))
    ) {
      form.setFieldsValue({ conversion_rate: Number(savedRate) });
      dispatch(setLiveConversionRate(Number(savedRate)));
    }
  }, [engagementData]);

  // ─── Sync mandays and subcontractor totals ───────────────────────────────────
  useEffect(() => {
    const data =
      engagementData?.employeeList ?? tempEngagementFormData?.resourceList ?? [];

    const latestCurrency = currencyValueList?.[0];

    const subcontractorData =
      engagementData?.subcontractor ?? tempEngagementFormData?.subcontractList ?? [];

    const sumSubContractamt = sumBy(subcontractorData, (item) => {
      const amount = isNaN(parseFloat(item?.amount)) ? 0 : parseFloat(item?.amount);

      if (!latestCurrency) return amount;

      const currencyKey = currencyKeyMap[item?.currency];
      const normalizedCurrency = Object.fromEntries(
        Object.entries(latestCurrency).map(([k, v]) => [k.toLowerCase(), v])
      );
      const rate = currencyKey
        ? parseFloat(normalizedCurrency[currencyKey.toLowerCase()]) || 1
        : 1;
      return amount * rate;
    });

    form.setFieldsValue({ subcontractor_usd: sumSubContractamt || 0 });

    // FIX #2: budjetChargeout was calculated but result was never stored via setBudjetChargeout
    const calculatedBudjetChargeout = (data || [])
      .filter((item) => {
        const resourceExtRate = parseFloat(item?.resourceExtRate);
        return !isNaN(resourceExtRate) && !isNaN(parseFloat(item?.personDay));
      })
      .map((item) => parseFloat(item?.resourceExtRate) * (parseFloat(item?.personDay) || 0) * 8)
      .reduce((acc, value) => acc + value, 0);

    setBudjetChargeout(calculatedBudjetChargeout);
    setPlannedSubcontractor(sumSubContractamt || 0);
  }, [engagementData, tempEngagementFormData, currencyValueList]);


  useEffect(() => {
    const data =
      engagementData?.employeeList ?? tempEngagementFormData?.resourceList ?? [];

    // mandays now comes from ResourcePlan's setTotalMandays callback — see below

    const latestCurrency = currencyValueList?.[0];

    const subcontractorData =
      engagementData?.subcontractor ?? tempEngagementFormData?.subcontractList ?? [];
    // ...rest unchanged (subcontractor_usd and budjetChargeout calc)
  }, [engagementData, tempEngagementFormData, currencyValueList]);

  // Mandays displayed = exactly what ResourcePlan computed from its own rendered rows.
  useEffect(() => {
    form.setFieldsValue({ mandays: tableMandays || 0 });
  }, [tableMandays]);

  const enableIfActiveAndStakeHolder = () => {
    if (disabled) {
      const engagementStatus = engagementData?.jobCloser?.status;
      const isPartnerOrManagerOrHeadOfficeUser =
        userInfo?.userid === engagementData?.stakeHolder?.personIncharge ||
        userInfo?.userid === engagementData?.stakeHolder?.partner ||
        engagementHigherUsers.includes(userInfo?.userid) ||
        userInfo?.roleId === "8";
      const isEngagementActive = engagementStatus === "Active";
      return update ? !(isPartnerOrManagerOrHeadOfficeUser && isEngagementActive) : false;
    }
    return false;
  };

  const currencyDisable = enableIfActiveAndStakeHolder();

  const { key: billingKey, url: billingUrl } = getBillingById(id);
  const { key: timesheetKey, url: timesheetUrl } = getTimeSheetByengagementId(id);

  const { data: billingList } = useFetch(billingKey, billingUrl, { enabled: !!id });
  const { data: timeSheetList } = useFetch(timesheetKey, timesheetUrl, { enabled: !!id });

  const billingData = useMemo(() => billingList?.response?.items ?? [], [billingList]);
  const timeSheetData = useMemo(() => timeSheetList?.response?.response ?? [], [timeSheetList]);

  // ─── Core profit calculation ─────────────────────────────────────────────────
  // Formula: Billing Amount (USD) - (Planned Chargeout + Planned Subcontractor)
  //
  // Always computes and displays the profit figure so the summary table is
  // correct on fresh load. Form field write-back, cache updates, and status
  // changes only happen when the user has actually modified financial fields,
  // to avoid stomping API-sourced values on a read-only view.
  function profitCals() {
    let usdAmount = form.getFieldValue("usd_amount");
    if (typeof usdAmount === "string") usdAmount = usdAmount.replace(/,/g, "");
    usdAmount = parseFloat(usdAmount || 0);

    // profitLoss = Billing Amount(USD) - (Planned Chargeout + Planned Subcontractor)
    const profitLossCal =
      usdAmount - (parseFloat(budjetChargeout || 0) + Number(plannedSubcontractor));

    // Always update the display — this is read-only state, safe on every render
    setProfitLoss(profitLossCal);

    // Only write back to form fields, cache, and status when user has modified values.
    // On a fresh/read-only load this block is skipped so API values are preserved.
    if (!hasUserModified.current) return null;

    const pfaStatus = profitLossCal < 0 ? "Not Approved" : "Not Applicable";

    const currentStatus = form.getFieldValue("status");
    const apiJobStatus = engagementData?.jobCloser?.status;
    const isLocked = apiJobStatus === "Active" || currentStatus === "Closed";
    const newStatus = pfaStatus === "Not Approved" ? "Not Approved" : "Not Started";

    form.setFieldsValue({
      planned_fee_adjustment: profitLossCal < 0 ? Math.abs(profitLossCal) : "0.00",
      pfa_usd: profitLossCal < 0 ? Math.abs(profitLossCal) : "0.00",
      pfa_status: pfaStatus,
      ...(!isLocked && { status: newStatus }),
    });

    const existing = userModifiedCache.get(id) || {};
    userModifiedCache.set(id, {
      ...existing,
      usd_amount: usdAmount,
      estimated_profit: profitLossCal,
      pfa_status: pfaStatus,
      status: !isLocked ? newStatus : currentStatus,
      planned_fee_adjustment: profitLossCal < 0 ? Math.abs(profitLossCal) : 0,
      pfa_usd: profitLossCal < 0 ? Math.abs(profitLossCal) : 0,
    });

    if (tempEngagementFormData) {
      tempEngagementFormData.usd_amount = usdAmount;
      tempEngagementFormData.estimated_profit = profitLossCal;
      tempEngagementFormData.pfa_status = pfaStatus;
      if (!isLocked) tempEngagementFormData.status = newStatus;
    }

    return pfaStatus;
  }

  // ─── Auto-recalc when dependencies change ────────────────────────────────────
  useEffect(() => {
    profitCals();
  }, [
    billingData,
    timeSheetData,
    engagementData,
    budjetChargeout,
    plannedSubcontractor,
  ]);

  // ─── On unmount: clear cache ONLY when navigating outside this engagement ────
  useEffect(() => {
    return () => {
      const nextPath = history.location.pathname;
      // The engagementId is no longer part of the URL (it's passed via router
      // state instead), so we can no longer distinguish "this engagement" from
      // "another engagement" by path alone. Any /engagement/* sub-route is
      // still considered part of the same in-progress engagement flow; only
      // clear the cache once the user navigates completely outside it.
      const isStillWithinEngagement = nextPath.startsWith(`/engagement`);
      if (!isStillWithinEngagement) {
        userModifiedCache.delete(id);
      }
    };
  }, [id]);

  const yearRangeOptions = [];
  const startYear = 2000;
  const endYear = new Date().getFullYear() + 1;
  for (let year = startYear; year < endYear; year++) {
    yearRangeOptions.push({ label: `${year}-${year + 1}`, value: `${year}-${year + 1}` });
  }

  const handleAddResource = () => {
    const planStartDate = formValues?.plan_start_date;
    const planEndDate = formValues?.plan_end_date;
    const personIncharge = formValues?.person_incharge;
    const partner = formValues?.partner;

    if (planStartDate && planEndDate && personIncharge && partner) {
      history.push("/engagement/add-resources", {
        id: engagementData?.engagementId ?? tempEngagementFormData?.engagement_id,
      });
    } else {
      notification.error({
        message: "Please Select Person In charge, Partner, Plan Start Date and Plan End Date",
      });
    }
  };

  const handleShowSubcontractorModal = () => {
    const planStartDate = formValues?.plan_start_date;
    const planEndDate = formValues?.plan_end_date;
    const personIncharge = formValues?.person_incharge;
    const partner = formValues?.partner;

    if (planStartDate && planEndDate && personIncharge && partner) {
      setShowSubContractorModal(true);
    } else {
      notification.error({
        message: "Please Select Person In charge, Partner, Plan Start Date and Plan End Date",
      });
    }
  };

  // FIX #4: nullish coalescing (??) has lower precedence than logical OR (||)
  // The original expression was parsed as:
  //   user.userid === (tempEngagementFormData?.partner ?? engagementData?.stakeHolder?.partner ?? null)
  // which is correct only by accident due to JS precedence. Made it explicit with parentheses.
  const selectedPartnerName = userList?.find((user) => {
    const partnerId =
      tempEngagementFormData?.partner ??
      engagementData?.stakeHolder?.partner ??
      null;
    return user.userid === partnerId;
  });

  const clientName = form.getFieldValue("client_name");

  const engagementApprovalSendEmail = () => {
    const sendEmailData = {
      managingPartner: selectedPartnerName
        ? selectedPartnerName?.firstname + " " + selectedPartnerName?.lastname
        : null,
      clientName: engagementData?.client?.clientName ?? clientName ?? null,
      emailId:
        engagementData?.stakeHolder?.partner ??
        tempEngagementFormData?.partner ??
        null,
    };
    addEngagement(sendEmailData, {
      onSuccess: () => {
        notification.success({ message: "Email Sent Successfully" });
      },
    });
  };

  const handleKeyDown = (e) => {
    const { key } = e;
    if (!/^\d$/.test(key) && key !== "Backspace" && key !== ".") {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const cached = userModifiedCache.get(id);

    if (cached?.usd_amount !== undefined) {
      const cachedAmount = cached.usd_amount;
      const formatted = currencyAmount("USD", cachedAmount).replace("USD", "");
      form.setFieldValue("usd_amount", formatted || String(cachedAmount));
    } else {
      let usdAmount = form.getFieldValue("usd_amount");
      if (!usdAmount) {
        form.setFieldValue("usd_amount", "0");
      } else {
        if (typeof usdAmount === "string") {
          usdAmount = usdAmount.replace(/,/g, "");
        }
        const parsedAmount = Number(usdAmount);
        if (!isNaN(parsedAmount)) {
          const formatted = currencyAmount("USD", parsedAmount).replace("USD", "");
          form.setFieldValue("usd_amount", formatted);
        } else {
          form.setFieldValue("usd_amount", "0");
        }
      }
    }

    if (update) {
      form.setFieldValue(
        "billing_office",
        engagementData?.financial_resourcePlan?.billingOffice
      );
    }
  }, []);

  useEffect(() => {
    form.setFieldValue(
      "billing_office",
      tempEngagementFormData?.billing_office ??
      engagementData?.financial_resourcePlan?.billingOffice
    );
  }, [update, engagementData, tempEngagementFormData, tabId]);

  useEffect(() => {
    const fetchCurrencyValues = async () => {
      try {
        const response = await CALL_FEATCH_API('insyts/timesheet/currencylist', 'get');
        const currencyResponse = response?.response?.data || [];
        setCurrencyValueList(currencyResponse);
        dispatch(setCurrencyList(currencyResponse));
      } catch (error) {
        console.error("Currency list fetch failed:", error);
      }
    };
    fetchCurrencyValues();
  }, []);

  // FIX #5: billing entity disabled logic had a broken operator precedence.
  // Original: (A && B || C || D) — the && binds tighter than ||, so it was
  // (A && B) || C || D, meaning C and D alone could enable the field for any user.
  // Wrapped the intended "is privileged user" check in explicit parentheses.
  const isBillingEntityEditable =
    (userInfo?.userid === engagementData?.stakeHolder?.personIncharge &&
      engagementData?.jobCloser?.status === "Active") ||
    engagementHigherUsers.includes(userInfo?.userid) ||
    userInfo?.roleId === "8";

  // FIX #6: conversion_rate field was always disabled when conversion_rate had any value
  // (!!getFieldValue("conversion_rate") is true as long as the field is non-empty/non-zero).
  // This meant users could never manually edit the rate after it was auto-populated.
  // Removed that condition; only addResourceandSubDisabled and Closed status lock the field.
  const isConversionRateDisabled =
    addResourceandSubDisabled ||
    engagementData?.jobCloser?.status === "Closed";

  return (
    <FinancialWrap>
      <Card className="fields-card">
        <Row gutter={24}>
          <Col span={12}>
            {/* FIX #5 applied here */}
            <Form.Item
              label={<span style={labelStyle}>Billing Entity <Required /></span>}
              name="billing_office"
              rules={[{ required: true, message: "Please select billing Entity!" }]}
            >
              <Select
                placeholder="Select"
                showSearch={true}
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase() ?? "").includes(input.toLowerCase())
                }
                options={billingOfficeOptions}
                disabled={isBillingEntityEditable ? false : disabled}
              />
            </Form.Item>

            <Form.Item
              label={<span style={labelStyle}>Financial Year <Required /></span>}
              name="financial_year"
            >
              <Input placeholder="Enter" disabled />
            </Form.Item>

            <Form.Item label={<span style={labelStyle}>Mandays</span>} name="mandays">
              <Input placeholder="Enter" disabled />
            </Form.Item>

            <Form.Item label={<span style={labelStyle}>Currency</span>} name="currency">
              <Select
                placeholder="Select"
                options={currencyOptions}
                showSearch={true}
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase() ?? "").includes(input.toLowerCase())
                }
                onChange={(value) => {
                  isUserCurrencyChange.current = true;
                  hasUserModified.current = true;
                  userModifiedCache.set(id, userModifiedCache.get(id) || {});
                  handleCurrencyChange(value);
                }}
                disabled={userInfo?.roleId === "2" ? false : currencyDisable}
              />
            </Form.Item>

            <Form.Item
              label={<span style={labelStyle}>Local Currency Amount</span>}
              name="billing_amount"
            >
              <Input
                placeholder="Enter"
                onChange={handleBillingAmountChange}
                disabled={
                  userInfo?.roleId === "2"
                    ? false
                    : localAddresourceDisable || engagementData?.jobCloser?.status === "Closed"
                }
                onKeyDown={handleKeyDown}
              />
            </Form.Item>

            {/* FIX #6 applied here — removed shouldUpdate wrapper (unnecessary for this use case)
                and replaced the always-blocking disabled condition */}
            <Form.Item
              label={<span style={labelStyle}>Conversion rate</span>}
              name="conversion_rate"
            >
              <Input
                placeholder="Enter"
                onChange={(e) => {
                  isUserCurrencyChange.current = true;
                  hasUserModified.current = true;
                  userModifiedCache.set(id, userModifiedCache.get(id) || {});
                  handleConversionRateChange(e);
                }}
                disabled={true}
                onKeyDown={handleKeyDown}
              />
            </Form.Item>

            <Form.Item label={<span style={labelStyle}>USD Amount</span>} name="usd_amount">
              <Input placeholder="Enter" disabled={true} />
            </Form.Item>

            <Form.Item name="planned_fee_adjustment" style={{ display: "none" }}>
              <Input type="hidden" />
            </Form.Item>

            <Form.Item name="pfa_usd" style={{ display: "none" }}>
              <Input type="hidden" />
            </Form.Item>

            <div className="d-flex align-items-start gap-2">
              <Form.Item
                label={<span style={labelStyle}>PFA Status</span>}
                name="pfa_status"
                className="w-100"
              >
                <Input placeholder="Enter" disabled={true} />
              </Form.Item>
              {profitLoss < 0 && (
                <Button
                  type="primary"
                  loading={isPosting}
                  onClick={engagementApprovalSendEmail}
                  disabled={form.getFieldValue("pfa_status") === "Approved"}
                >
                  Get Approval
                </Button>
              )}
            </div>

            <Form.Item
              style={{ display: "none" }}
              label={<span style={labelStyle}>Subcontractor USD</span>}
              name="subcontractor_usd"
            >
              <Input placeholder="Enter" type="hidden" disabled={true} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <StyleTable
              bordered
              columns={[
                { title: "Summary", dataIndex: "summary", key: "summary" },
                {
                  title: "Budget(USD)",
                  dataIndex: "manday_fee",
                  key: "manday_fee",
                  className: "align-right",
                },
              ]}
              dataSource={[
                { key: "1", summary: "Total Mandays", manday_fee: formValues?.mandays || "0" },
                {
                  key: "2",
                  summary: "Billing Amount(USD)",
                  manday_fee:
                    currencyAmount("$", parseFloat(
                      typeof formValues?.usd_amount === "string"
                        ? formValues?.usd_amount?.replace(/,/g, "")
                        : formValues?.usd_amount || 0
                    )) || currencyAmount("$", 0),
                },
                {
                  key: "3",
                  summary: "Planned Chargeout",
                  manday_fee: currencyAmount("$", budjetChargeout) || currencyAmount("$", 0),
                },
                {
                  key: "4",
                  summary: "Planned Subcontractor",
                  // FIX #3: use Number() to ensure numeric formatting, not string concat
                  manday_fee: currencyAmount("$", Number(plannedSubcontractor)) || currencyAmount("$", 0),
                },
                {
                  key: "5",
                  summary: "Estimated Profit",
                  manday_fee: (
                    <span style={{ color: profitLoss < 0 ? "red" : "black" }}>
                      {currencyAmount("$", profitLoss)}
                    </span>
                  ),
                },
                {
                  key: "6",
                  summary: "PFA(Planned fee Adjustment)",
                  manday_fee:
                    profitLoss < 0
                      ? currencyAmount("$", Math.abs(profitLoss))
                      : currencyAmount("$", 0),
                },
                {
                  key: "7",
                  summary: "Profit - after PFA",
                  // FIX: removed budjetChargeout > 0 gate — same issue as Estimated Profit.
                  // Profit after PFA = profitLoss when positive (no PFA applied).
                  manday_fee:
                    profitLoss > 0
                      ? currencyAmount("$", Math.abs(profitLoss))
                      : currencyAmount("$", 0),
                },
              ]}
              pagination={false}
            />
          </Col>
        </Row>
      </Card>

      <div className="mt-3">
        {!localAddresourceDisable && (
          <div className="btns">
            <Button
              type="primary"
              className="add-resource-btn"
              onClick={handleAddResource}
              disabled={
                userInfo?.roleId === "2"
                  ? false
                  : localAddresourceDisable || engagementData?.jobCloser?.status === "Closed"
              }
            >
              Add Resources
            </Button>
            <Button
              type="primary"
              className="add-subcontractor-btn"
              onClick={handleShowSubcontractorModal}
              disabled={
                userInfo?.roleId === "2"
                  ? false
                  : localAddresourceDisable || engagementData?.jobCloser?.status === "Closed"
              }
            >
              Add Subcontractor
            </Button>
          </div>
        )}
        <ResourcePlan
          engagementData={engagementData}
          tempEngagementFormData={tempEngagementFormData}
          engagementRefetch={engagementRefetch}
          update={update}
          setDeletedbookedUserId={setDeletedbookedUserId}
          addResourceandSubDisabled={addResourceandSubDisabled}
          localAddresourceDisable={localAddresourceDisable}
          setBudjetChargeout={setBudjetChargeout}
          setIsResource={setIsResource}
          setTotalMandays={setTableMandays}
        />
        <SubContractor
          engagementData={engagementData}
          setShowAddModal={setShowSubContractorModal}
          showAddModal={showSubContractorModal}
          tempEngagementFormData={tempEngagementFormData}
          engagementRefetch={engagementRefetch}
        />
      </div>
    </FinancialWrap>
  );
};

export default FinancialResourcePlanFields;

const FinancialWrap = styled.div`
  .btns {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
  }
  .add-resource-btn,
  .add-subcontractor-btn {
    border-radius: 5px;
  }
  .ant-select-selector {
    cursor: pointer !important;
  }
`;

const StyleTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: #dbdbdb;
    font-weight: 700;
  }
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #dbdbdb;
  }
  .ant-table.ant-table-bordered > .ant-table-container > .ant-table-content > table > thead > tr > th {
    border-right: 1px solid #dbdbdb;
  }
  .ant-table.ant-table-bordered > .ant-table-container > .ant-table-content > table > tbody > tr > td {
    border-right: 1px solid #dbdbdb;
  }
  .ant-table.ant-table-bordered > .ant-table-container > .ant-table-content > table {
    border-top: 1px solid #dbdbdb;
  }
  .ant-table.ant-table-bordered > .ant-table-container {
    border-left: 1px solid #dbdbdb;
  }
  .ant-table-cell {
    padding: 6px 12px;
    font-size: 16px;
    font-weight: 500;
  }
  .ant-table-container {
    border-radius: 5px;
  }
  .ant-table {
    border-radius: 5px;
  }
  .align-right {
    text-align: right;
  }
`;